import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  Button,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  MenuItem,
  Typography,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Stack,
  CircularProgress,
  Radio,
  Drawer,
  Paper,
  Divider
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useGridApiRef } from '@mui/x-data-grid';
import ErpDataGrid, { GeneralItemsGridFrame } from '../../components/common/ErpDataGrid';
import ListDetailOpenModeBar from '../../components/common/ListDetailOpenModeBar';
import useListDetailOpenMode from '../../hooks/useListDetailOpenMode';
import { JobDetailsPanel } from './JobDetails';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Public,
  PublicOff,
  Block as CloseJobIcon,
  Cancel as CancelJobIcon,
  PersonAdd as PersonAddIcon,
  OpenInNew as OpenInNewIcon,
  PictureAsPdf as PdfIcon,
  ContentCopy as CopyIcon,
  VisibilityOff as CollapseAssigneesIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { formatJobLocationLine } from '../../utils/locationDisplay';
import { useAuth } from '../../context/AuthContext';
import { standardAuditColumns } from '../../utils/auditGridColumns';
import { copyAppPathToClipboard, openAppPathInNewTab } from '../../utils/listScreenHelpers';
import ListRowActionsBar from '../../components/common/ListRowActionsBar';

const cellTooltip = (value) => (
  <Tooltip title={value != null && value !== '' ? String(value) : ''} placement="top-start" enterDelay={400}>
    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', width: '100%' }}>{value ?? ''}</span>
  </Tooltip>
);

function ownerAssignmentRole(owner) {
  if (!owner || typeof owner !== 'object') return 'secondary';
  const j = owner.job_requirement_owners || owner.JobRequirementOwner || {};
  return j.assignment_role === 'primary' ? 'primary' : 'secondary';
}

/** Filter chips on /jobs (synced to ?assignment= / ?candidates=). Each control has default vs selected background. */
const JOB_LIST_VIEWS = [
  { value: 'all', label: 'All jobs', assignment: '', candidates: '', theme: { color: '#3730a3', defaultBg: 'rgba(79, 70, 229, 0.32)', selectedBg: '#4f46e5', selectedColor: '#ffffff' } },
  { value: 'unassigned', label: 'Unassigned Jobs', assignment: 'unassigned', candidates: '', theme: { color: '#334155', defaultBg: 'rgba(100, 116, 139, 0.38)', selectedBg: '#475569', selectedColor: '#ffffff' } },
  { value: 'assign_1', label: 'Assigned Jobs +1', assignment: '1', candidates: '', theme: { color: '#115e59', defaultBg: 'rgba(13, 148, 136, 0.35)', selectedBg: '#0d9488', selectedColor: '#ffffff' } },
  { value: 'assign_2', label: 'Assigned Jobs +2', assignment: '2', candidates: '', theme: { color: '#155e75', defaultBg: 'rgba(8, 145, 178, 0.35)', selectedBg: '#0891b2', selectedColor: '#ffffff' } },
  { value: 'assign_3', label: 'Assigned Jobs +3', assignment: '3', candidates: '', theme: { color: '#1e40af', defaultBg: 'rgba(37, 99, 235, 0.35)', selectedBg: '#2563eb', selectedColor: '#ffffff' } },
  { value: 'assign_many', label: 'Assigned Jobs ++', assignment: 'many', candidates: '', theme: { color: '#5b21b6', defaultBg: 'rgba(124, 58, 237, 0.32)', selectedBg: '#7c3aed', selectedColor: '#ffffff' } },
  { value: 'cand_0', label: 'Jobs With No Candidate', assignment: '', candidates: 'none', theme: { color: '#92400e', defaultBg: 'rgba(180, 83, 9, 0.38)', selectedBg: '#b45309', selectedColor: '#ffffff' } },
  { value: 'cand_1', label: 'Jobs With 1 Candidate', assignment: '', candidates: '1', theme: { color: '#854d0e', defaultBg: 'rgba(202, 138, 4, 0.38)', selectedBg: '#ca8a04', selectedColor: '#1c1917' } },
  { value: 'cand_2', label: 'Jobs With 2 Candidates', assignment: '', candidates: '2', theme: { color: '#3f6212', defaultBg: 'rgba(101, 163, 13, 0.38)', selectedBg: '#65a30d', selectedColor: '#ffffff' } },
  { value: 'cand_3', label: 'Jobs With 3 Candidates', assignment: '', candidates: '3', theme: { color: '#14532d', defaultBg: 'rgba(21, 128, 61, 0.38)', selectedBg: '#15803d', selectedColor: '#ffffff' } },
  { value: 'cand_many', label: 'Jobs With ++ Candidates', assignment: '', candidates: 'many', theme: { color: '#9d174d', defaultBg: 'rgba(190, 24, 93, 0.35)', selectedBg: '#be185d', selectedColor: '#ffffff' } }
];

const PRIORITY_ROW_BG = {
  critical: 'rgba(239, 68, 68, 0.16)',
  high: 'rgba(245, 158, 11, 0.12)',
  medium: 'rgba(59, 130, 246, 0.08)',
  low: 'rgba(16, 185, 129, 0.08)'
};

/** Job lifecycle (API status filter). Default when URL omits param: active only. */
const JOB_LIFECYCLE_VIEWS = [
  { value: 'active', label: 'All Active Jobs', theme: { color: '#047857', defaultBg: 'rgba(16, 185, 129, 0.28)', selectedBg: '#059669', selectedColor: '#ffffff' } },
  { value: 'closed', label: 'All Closed Jobs', theme: { color: '#475569', defaultBg: 'rgba(71, 85, 105, 0.32)', selectedBg: '#64748b', selectedColor: '#ffffff' } },
  { value: 'cancelled', label: 'All Cancelled Jobs', theme: { color: '#b91c1c', defaultBg: 'rgba(239, 68, 68, 0.22)', selectedBg: '#dc2626', selectedColor: '#ffffff' } },
  { value: 'all', label: 'Any status', theme: { color: '#4338ca', defaultBg: 'rgba(79, 70, 229, 0.22)', selectedBg: '#4f46e5', selectedColor: '#ffffff' } }
];

function jobListViewFromParams(assignment, candidates) {
  const a = assignment || '';
  const c = candidates || '';
  const row = JOB_LIST_VIEWS.find((v) => v.assignment === a && v.candidates === c);
  return row ? row.value : 'all';
}

const JobsList = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission, isAdmin } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ priority: '' });
  const [lookupOptions, setLookupOptions] = useState({ priority: [], status: [] });
  const [pagination, setPagination] = useState({ page: 0, pageSize: 10, total: 0 });
  const [selectedJob, setSelectedJob] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [jobPickerOptions, setJobPickerOptions] = useState([]);
  const [jobPickerLoading, setJobPickerLoading] = useState(false);
  const [jobForCloseCancel, setJobForCloseCancel] = useState(null);
  const [statusDialog, setStatusDialog] = useState(null);
  const [statusSaving, setStatusSaving] = useState(false);
  const [assignmentMeta, setAssignmentMeta] = useState(null);
  const [assigningJobId, setAssigningJobId] = useState(null);
  const [requestingJobId, setRequestingJobId] = useState(null);
  const [pendingAssignmentRequests, setPendingAssignmentRequests] = useState([]);
  const [outgoingPendingJobIds, setOutgoingPendingJobIds] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [singleJobId, setSingleJobId] = useState(null);
  const [closureReason, setClosureReason] = useState('');
  const { mode: detailOpenMode, persistMode: persistDetailOpenMode, popupId, setPopupId, sideId, setSideId, openRow: openJobRow } =
    useListDetailOpenMode('jobs');
  const [highlightJobId, setHighlightJobId] = useState(null);
  const [expandedAssigneeRows, setExpandedAssigneeRows] = useState(() => new Set());
  const apiRef = useGridApiRef();
  const pickerScrollPendingRef = useRef(null);
  const pickerSearchTimer = useRef(null);

  const assignment = searchParams.get('assignment') || '';
  const candidates = searchParams.get('candidates') || '';
  const lifecycleParam = searchParams.get('lifecycle');
  const effectiveLifecycle = ['active', 'closed', 'cancelled', 'all'].includes(lifecycleParam)
    ? lifecycleParam
    : 'active';

  /** Resolved tab + API params (unknown query combos fall back to All). */
  const listViewRow = useMemo(() => {
    const key = jobListViewFromParams(assignment, candidates);
    return JOB_LIST_VIEWS.find((v) => v.value === key) || JOB_LIST_VIEWS[0];
  }, [assignment, candidates]);

  const activeListView = listViewRow.value;
  const effectiveAssignment = listViewRow.assignment;
  const effectiveCandidates = listViewRow.candidates;

  const handleSelectListView = (value) => {
    const row = JOB_LIST_VIEWS.find((v) => v.value === value);
    if (!row) return;
    setPagination((p) => ({ ...p, page: 0 }));
    const next = new URLSearchParams(searchParams);
    if (!row.assignment && !row.candidates) {
      next.delete('assignment');
      next.delete('candidates');
    } else {
      if (row.assignment) next.set('assignment', row.assignment);
      else next.delete('assignment');
      if (row.candidates) next.set('candidates', row.candidates);
      else next.delete('candidates');
    }
    setSearchParams(next, { replace: true });
  };

  const handleSelectLifecycle = (lc) => {
    setPagination((p) => ({ ...p, page: 0 }));
    const next = new URLSearchParams(searchParams);
    if (lc === 'active') {
      next.delete('lifecycle');
    } else {
      next.set('lifecycle', lc);
    }
    setSearchParams(next, { replace: true });
  };

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page + 1,
        limit: pagination.pageSize
      };
      const q = String(search || '').trim();
      if (q) params.search = q;
      if (filters.priority) params.priority = filters.priority;
      const lc = ['active', 'closed', 'cancelled', 'all'].includes(lifecycleParam) ? lifecycleParam : 'active';
      if (lc !== 'all') params.status = lc;
      if (effectiveAssignment) params.assignment = effectiveAssignment;
      if (effectiveCandidates) params.candidates = effectiveCandidates;

      const response = await api.get('/jobs', { params });
      setJobs(response.data.data);
      setPagination((prev) => ({ ...prev, total: response.data.pagination.total }));
    } catch (error) {
      console.error('Fetch error:', error);
      // api.js interceptor already toasts; avoid duplicate
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, search, filters, effectiveAssignment, effectiveCandidates, lifecycleParam]);

  const fetchJobPickerOptions = useCallback(async (searchText) => {
    setJobPickerLoading(true);
    try {
      const params = { page: 1, limit: 80 };
      const q = String(searchText || '').trim();
      if (q) params.search = q;
      const r = await api.get('/jobs', { params });
      setJobPickerOptions(Array.isArray(r.data?.data) ? r.data.data : []);
    } catch {
      setJobPickerOptions([]);
    } finally {
      setJobPickerLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobPickerOptions('');
  }, [fetchJobPickerOptions]);

  const schedulePickerSearch = (input) => {
    if (pickerSearchTimer.current) clearTimeout(pickerSearchTimer.current);
    pickerSearchTimer.current = setTimeout(() => fetchJobPickerOptions(input), 280);
  };

  useEffect(() => {
    setPagination((p) => ({ ...p, page: 0 }));
  }, [effectiveAssignment, effectiveCandidates]);

  const scrollPickerJobIntoView = useCallback(
    (job) => {
      if (!job?.id) return;
      const idx = jobs.findIndex((j) => j.id === job.id);
      if (idx >= 0) {
        requestAnimationFrame(() => {
          apiRef.current?.scrollToIndexes?.({ rowIndex: idx, colIndex: 0 });
          setHighlightJobId(job.id);
          window.setTimeout(() => setHighlightJobId(null), 2200);
        });
      } else {
        pickerScrollPendingRef.current = job.id;
        setJobs((prev) => {
          if (prev.some((j) => j.id === job.id)) return prev;
          return [job, ...prev];
        });
      }
    },
    [jobs, apiRef]
  );

  useEffect(() => {
    const pendingId = pickerScrollPendingRef.current;
    if (!pendingId || !apiRef.current) return;
    const idx = jobs.findIndex((j) => j.id === pendingId);
    if (idx < 0) return;
    pickerScrollPendingRef.current = null;
    requestAnimationFrame(() => {
      apiRef.current?.scrollToIndexes?.({ rowIndex: idx, colIndex: 0 });
      setHighlightJobId(pendingId);
      window.setTimeout(() => setHighlightJobId(null), 2200);
    });
  }, [jobs, apiRef]);

  const handleOpenJobDetails = useCallback((row) => openJobRow(row, (r) => `/jobs/${r.id}`), [openJobRow]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  useEffect(() => {
    if (isAdmin) {
      if (rowSelectionModel.length === 1) {
        const id = rowSelectionModel[0];
        const j = jobs.find((x) => x.id === id);
        setJobForCloseCancel(j || null);
      } else {
        setJobForCloseCancel(null);
      }
    } else if (singleJobId) {
      const j = jobs.find((x) => x.id === singleJobId);
      setJobForCloseCancel(j || null);
    } else {
      setJobForCloseCancel(null);
    }
  }, [isAdmin, rowSelectionModel, singleJobId, jobs]);

  useEffect(() => {
    api
      .get('/jobs/lookups')
      .then((r) => {
        const opts = r.data?.data?.options || {};
        setLookupOptions({
          priority: Array.isArray(opts.priority) ? opts.priority : [],
          status: Array.isArray(opts.status) ? opts.status : []
        });
      })
      .catch(() => {
        setLookupOptions({ priority: [], status: [] });
      });
  }, []);

  useEffect(() => {
    api
      .get('/jobs/assignment-meta')
      .then((r) => setAssignmentMeta(r.data?.data ?? null))
      .catch(() => setAssignmentMeta(null));
  }, []);

  const refreshAssignmentRequests = useCallback(async () => {
    try {
      const [incoming, outgoing] = await Promise.all([
        api.get('/jobs/assignment-requests/pending-for-me'),
        api.get('/jobs/assignment-requests/my-outgoing')
      ]);
      setPendingAssignmentRequests(incoming.data?.data?.requests ?? []);
      setOutgoingPendingJobIds(outgoing.data?.data?.jobIds ?? []);
    } catch {
      setPendingAssignmentRequests([]);
      setOutgoingPendingJobIds([]);
    }
  }, []);

  useEffect(() => {
    refreshAssignmentRequests();
  }, [refreshAssignmentRequests]);

  const handleTogglePublicListing = async (e, row) => {
    e.stopPropagation();
    if (!hasPermission('jobs_modify')) return;
    const isPublic = String(row.listing_visibility || '').toLowerCase() === 'public';
    const next = isPublic ? 'private' : 'public';
    try {
      await api.put(`/jobs/${row.id}`, { listing_visibility: next });
      toast.success(
        next === 'public'
          ? 'Job will appear on the public portal when tenant Jobs visibility is Public.'
          : 'Job removed from the public portal.'
      );
      fetchJobs();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not update public listing');
    }
  };

  const handleDelete = async () => {
    if (!selectedJob) return;
    try {
      const res = await api.delete(`/jobs/${selectedJob.id}`, { skipErrorToast: true });
      toast.success(res.data?.message || 'Job removed.');
      fetchJobs();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to delete job');
    }
    setDeleteDialog(false);
    setSelectedJob(null);
  };

  const handleAssignToMe = async (row) => {
    if (!row?.id || assigningJobId) return;
    setAssigningJobId(row.id);
    try {
      const res = await api.post(`/jobs/${row.id}/assign-me`);
      toast.success(res.data?.message || 'Assignment updated.');
      fetchJobs();
      refreshAssignmentRequests();
    } catch {
      /* 409 etc.: interceptor shows server message */
    } finally {
      setAssigningJobId(null);
    }
  };

  const handleRequestAssignment = async (row) => {
    if (!row?.id || requestingJobId) return;
    setRequestingJobId(row.id);
    try {
      const res = await api.post(`/jobs/${row.id}/request-assign`);
      toast.success(res.data?.message || 'Request submitted.');
      fetchJobs();
      refreshAssignmentRequests();
    } catch {
      /* interceptor */
    } finally {
      setRequestingJobId(null);
    }
  };

  const handleDecideAssignmentRequest = async (requestId, approve) => {
    try {
      await api.post(`/jobs/assignment-requests/${requestId}/decide`, { approve });
      toast.success(approve ? 'Assignment approved.' : 'Request declined.');
      refreshAssignmentRequests();
      fetchJobs();
    } catch {
      /* interceptor */
    }
  };

  const applyJobStatus = async () => {
    if (!jobForCloseCancel || !statusDialog) return;
    const reason = String(closureReason || '').trim();
    if (!reason) {
      toast.error('Please enter a reason for closing or cancelling this job.');
      return;
    }
    setStatusSaving(true);
    try {
      await api.put(`/jobs/${jobForCloseCancel.id}`, { status: statusDialog, closure_reason: reason });
      toast.success(statusDialog === 'closed' ? 'Job marked closed.' : 'Job marked cancelled.');
      setStatusDialog(null);
      setClosureReason('');
      setJobForCloseCancel(null);
      setRowSelectionModel([]);
      setSingleJobId(null);
      fetchJobs();
      fetchJobPickerOptions('');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not update job status.');
    } finally {
      setStatusSaving(false);
    }
  };

  const priorityColors = { critical: '#dc2626', high: '#d97706', medium: '#2563eb', low: '#059669' };
  const statusColors = {
    active: '#10b981',
    on_hold: '#f59e0b',
    filled: '#3b82f6',
    closed: '#6b7280',
    cancelled: '#ef4444',
    deleted: '#b45309',
    archived: '#7c3aed',
    draft: '#64748b'
  };

  const statusDisplay = (row) => {
    if (row.archived_at) return { label: 'Archived', key: 'archived' };
    if (row.status === 'deleted' || row.is_marked_for_deletion) return { label: 'Deleted', key: 'deleted' };
    const s = row.status || 'active';
    const label =
      (statusLabelMap[s] || (s ? String(s).replace(/_/g, ' ') : '—')).trim();
    return { label: label ? label.charAt(0).toUpperCase() + label.slice(1) : '—', key: s };
  };
  const priorityLabelMap = Object.fromEntries((lookupOptions.priority || []).map((x) => [x.code, x.label]));
  const statusLabelMap = Object.fromEntries((lookupOptions.status || []).map((x) => [x.code, x.label]));

  const pickColumn = !isAdmin
    ? [
        {
          field: '__pick',
          headerName: '',
          width: 52,
          minWidth: 52,
          maxWidth: 52,
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          renderCell: (params) => (
            <Box
              onClick={(e) => e.stopPropagation()}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}
            >
              <Radio
                size="small"
                checked={singleJobId === params.row.id}
                onChange={() => setSingleJobId(params.row.id)}
                value={params.row.id}
                inputProps={{ 'aria-label': `Select job ${params.row.job_code || params.row.id}` }}
              />
            </Box>
          )
        }
      ]
    : [];

  const columns = [
    ...pickColumn,
    {
      field: 'job_code',
      headerName: 'Reference',
      width: 240,
      minWidth: 180,
      hideable: false,
      renderCell: (p) => cellTooltip(p.value)
    },
    { field: 'title', headerName: 'Title', flex: 1, minWidth: 180, renderCell: (p) => cellTooltip(p.value) },
    {
      field: 'client_name',
      headerName: 'Client',
      width: 200,
      minWidth: 140,
      valueGetter: (params) => params.row.client?.company_name ?? 'N/A',
      renderCell: (p) => cellTooltip(p.value)
    },
    {
      field: 'priority',
      headerName: 'Priority',
      width: 120,
      minWidth: 100,
      renderCell: (params) => {
        const v = params.value;
        const bg = priorityColors[v] || '#94a3b8';
        const label = priorityLabelMap[v] || (v || '—').toString().toUpperCase();
        const isCrit = v === 'critical';
        return (
          <Chip
            label={label}
            size="small"
            sx={{
              bgcolor: alpha(bg, 0.22),
              color: bg,
              fontWeight: isCrit ? 800 : 600,
              fontSize: '0.7rem',
              border: isCrit ? `2px solid ${bg}` : 'none'
            }}
          />
        );
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      minWidth: 100,
      renderCell: (params) => {
        const { label, key } = statusDisplay(params.row);
        const bg = statusColors[key] || '#94a3b8';
        return <Chip label={label} size="small" sx={{ bgcolor: `${bg}20`, color: bg, fontWeight: 500, fontSize: '0.7rem' }} />;
      }
    },
    {
      field: 'location',
      headerName: 'Location',
      width: 200,
      minWidth: 120,
      valueGetter: (params) => formatJobLocationLine(params.row) || '',
      renderCell: (p) => cellTooltip(p.value),
    },
    {
      field: 'listing_visibility',
      headerName: 'Public',
      width: 100,
      minWidth: 90,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const pub = String(params.row.listing_visibility || '').toLowerCase() === 'public';
        if (!hasPermission('jobs_modify')) {
          return pub ? <Public fontSize="small" color="success" /> : <PublicOff fontSize="small" color="disabled" />;
        }
        return (
          <Tooltip
            title={
              pub
                ? 'Listed on public careers page (when tenant Jobs visibility is Public). Click to remove.'
                : 'Not on public page. Click to publish.'
            }
          >
            <IconButton size="small" onClick={(e) => handleTogglePublicListing(e, params.row)} color={pub ? 'success' : 'default'}>
              {pub ? <Public fontSize="small" /> : <PublicOff fontSize="small" />}
            </IconButton>
          </Tooltip>
        );
      }
    },
    {
      field: 'assignees',
      headerName: 'Assign to',
      width: 220,
      minWidth: 160,
      sortable: false,
      renderCell: (params) => {
        const owners = params.row.owners || [];
        const jobId = params.row.id != null ? String(params.row.id) : '';
        const expanded = jobId ? expandedAssigneeRows.has(jobId) : false;

        const nameLine = (o) => {
          const name = [o.first_name, o.last_name].filter(Boolean).join(' ').trim() || o.email || '—';
          const tag = ownerAssignmentRole(o) === 'primary' ? ' · Primary' : ' · Secondary';
          return `${name}${tag}`;
        };

        if (!owners.length) {
          return (
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
              —
            </Typography>
          );
        }

        if (owners.length === 1) {
          return (
            <Typography variant="caption" sx={{ lineHeight: 1.35, display: 'block', alignSelf: 'center' }}>
              {nameLine(owners[0])}
            </Typography>
          );
        }

        const primaryOwner = owners.find((o) => ownerAssignmentRole(o) === 'primary') || owners[0];
        const primaryLine = nameLine(primaryOwner);

        const toggle = (e) => {
          e.stopPropagation();
          if (!jobId) return;
          setExpandedAssigneeRows((prev) => {
            const next = new Set(prev);
            if (next.has(jobId)) next.delete(jobId);
            else next.add(jobId);
            return next;
          });
        };

        if (!expanded) {
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                width: '100%',
                minWidth: 0,
                py: 0.25,
              }}
            >
              <Typography variant="caption" sx={{ lineHeight: 1.35, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {primaryLine}
              </Typography>
              <Tooltip title="Show all assignees">
                <IconButton size="small" onClick={toggle} sx={{ p: 0.35 }} aria-label="Show all assignees">
                  <ViewIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          );
        }

        return (
          <Box
            sx={{
              py: 0.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.35,
              alignItems: 'flex-start',
              justifyContent: 'center',
              width: '100%',
              whiteSpace: 'normal',
            }}
          >
            {owners.map((o, i) => (
              <Typography key={`${jobId}-o-${o.id || i}`} variant="caption" sx={{ lineHeight: 1.35, display: 'block' }}>
                {nameLine(o)}
              </Typography>
            ))}
            <Tooltip title="Show primary assignee only">
              <IconButton size="small" onClick={toggle} sx={{ p: 0.35, alignSelf: 'flex-start' }} aria-label="Collapse assignees">
                <CollapseAssigneesIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: 'assignToMe',
      headerName: 'Assign',
      width: 168,
      minWidth: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const owners = params.row.owners || [];
        const myId = assignmentMeta?.employeeContactId;
        const max = assignmentMeta?.maxAssigneesPerJob ?? 3;
        const already = Boolean(myId && owners.some((o) => o.id === myId));
        const atCap = owners.length >= max;
        const busyAssign = assigningJobId === params.row.id;
        const busyReq = requestingJobId === params.row.id;
        const pendingOutgoing = Boolean(myId && outgoingPendingJobIds.includes(params.row.id));
        let tipAssign = 'Assign this job to yourself (first assignee becomes Primary).';
        if (!myId) tipAssign = 'Your login has no employee contact linked.';
        else if (already) tipAssign = 'You are already assigned.';
        else if (atCap) tipAssign = `Direct assignee limit (${max}) reached — request assignment instead.`;
        const disabledAssign =
          !hasPermission('jobs_view') || !myId || already || atCap || busyAssign || pendingOutgoing;
        let tipReq = 'Ask the primary assignee, a manager, or an administrator to approve joining this job.';
        if (!myId) tipReq = 'Your login has no employee contact linked.';
        else if (already) tipReq = 'You are already assigned.';
        else if (!atCap) tipReq = 'Use Assign to me while slots are available.';
        else if (pendingOutgoing) tipReq = 'Your request is pending approval.';
        const disabledReq =
          !hasPermission('jobs_view') || !myId || already || !atCap || busyReq || pendingOutgoing;
        return (
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'center', gap: 0.5, py: 0.25 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Tooltip title={tipAssign}>
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={disabledAssign}
                  startIcon={
                    busyAssign ? <CircularProgress color="inherit" size={14} /> : <PersonAddIcon sx={{ fontSize: 18 }} />
                  }
                  onClick={() => handleAssignToMe(params.row)}
                  sx={{ textTransform: 'none', fontSize: '0.7rem', py: 0.2, whiteSpace: 'nowrap', width: '100%' }}
                >
                  {busyAssign ? '…' : 'Assign to me'}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={tipReq}>
              <span>
                <Button
                  size="small"
                  variant="text"
                  disabled={disabledReq}
                  startIcon={busyReq ? <CircularProgress color="inherit" size={14} /> : null}
                  onClick={() => handleRequestAssignment(params.row)}
                  sx={{ textTransform: 'none', fontSize: '0.68rem', py: 0.1, whiteSpace: 'nowrap', width: '100%' }}
                >
                  {pendingOutgoing ? 'Pending approval' : busyReq ? '…' : 'Request assignment'}
                </Button>
              </span>
            </Tooltip>
          </Box>
        );
      }
    },
    {
      field: 'totalSubmissions',
      headerName: 'Candidates',
      width: 110,
      minWidth: 90,
      type: 'number',
      valueGetter: (params) => params.row.totalSubmissions ?? 0,
      renderCell: (p) => cellTooltip(String(p.value ?? 0))
    },
    ...standardAuditColumns(),
    { field: 'actions', headerName: 'Actions', width: 260, minWidth: 220, sortable: false, filterable: false, disableColumnMenu: true, renderCell: (params) => {
      const row = params.row;
      const path = `/jobs/${row.id}`;
      return (
        <ListRowActionsBar
          menuItems={[
            {
              label: 'Open in new tab',
              icon: <OpenInNewIcon fontSize="small" />,
              onClick: () => openAppPathInNewTab(path),
            },
            {
              label: 'Copy link',
              icon: <CopyIcon fontSize="small" />,
              onClick: () => copyAppPathToClipboard(path),
            },
          ]}
        >
          {hasPermission('jobs_modify') && (
            <Tooltip title="Edit">
              <IconButton size="small" color="primary" onClick={() => navigate(`${path}/edit`)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="View"><IconButton size="small" onClick={() => handleOpenJobDetails(row)}><ViewIcon fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Open for print or Save as PDF"><IconButton size="small" color="secondary" onClick={() => openAppPathInNewTab(path)}><PdfIcon fontSize="small" /></IconButton></Tooltip>
          {hasPermission('jobs_delete') && (
            <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => { setSelectedJob(row); setDeleteDialog(true); }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
          )}
        </ListRowActionsBar>
      );
    } }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 280px' }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Job Requirements</Typography>
          <Typography variant="body2" color="text.secondary">
            Use the lifecycle row for Active / Closed / Cancelled lists. {isAdmin ? 'Admins: select rows with checkboxes.' : 'Select one job with the radio to queue Close/Cancel (or use the job search).'} Assign yourself from each row when allowed.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ justifyContent: 'flex-end' }}>
          {hasPermission('jobs_modify') && (
            <>
              <Autocomplete
                size="small"
                sx={{ minWidth: { xs: '100%', sm: 280 }, maxWidth: 360 }}
                options={jobPickerOptions}
                loading={jobPickerLoading}
                value={jobForCloseCancel}
                onChange={(_e, v) => {
                  setJobForCloseCancel(v);
                  if (v?.id) {
                    if (isAdmin) setRowSelectionModel([v.id]);
                    else setSingleJobId(v.id);
                    scrollPickerJobIntoView(v);
                  }
                }}
                onInputChange={(_e, input, reason) => {
                  if (reason === 'input') schedulePickerSearch(input);
                }}
                onOpen={() => fetchJobPickerOptions('')}
                getOptionLabel={(o) => (o ? `${o.job_code || ''} · ${o.title || ''}`.trim() : '')}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Choose job"
                    placeholder="Search reference or title…"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {jobPickerLoading ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
              />
              <Button
                variant="outlined"
                color="secondary"
                size="medium"
                startIcon={<CloseJobIcon />}
                disabled={!jobForCloseCancel}
                onClick={() => setStatusDialog('closed')}
              >
                Close
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="medium"
                startIcon={<CancelJobIcon />}
                disabled={!jobForCloseCancel}
                onClick={() => setStatusDialog('cancelled')}
              >
                Cancelled
              </Button>
            </>
          )}
          {hasPermission('jobs_add') && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/jobs/new')} sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}>
              Add Job
            </Button>
          )}
        </Stack>
      </Box>

      <Box sx={{ width: '100%' }}>
      <Card sx={{ mb: 2 }} variant="outlined">
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            p: 1.25,
            bgcolor: alpha(theme.palette.background.default, theme.palette.mode === 'dark' ? 0.35 : 0.55),
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.85)}`
          }}
        >
          {JOB_LIFECYCLE_VIEWS.map((v) => {
            const sel = effectiveLifecycle === v.value;
            const th = v.theme;
            const borderCol = alpha(th.color, sel ? 0.55 : 0.4);
            return (
              <Button
                key={v.value}
                size="small"
                onClick={() => handleSelectLifecycle(v.value)}
                sx={{
                  textTransform: 'none',
                  fontWeight: sel ? 700 : 600,
                  fontSize: '0.78rem',
                  lineHeight: 1.25,
                  color: sel ? (th.selectedColor || '#fff') : th.color,
                  bgcolor: sel ? th.selectedBg : th.defaultBg,
                  border: `1px solid ${borderCol}`,
                  borderRadius: 1,
                  py: 0.85,
                  px: 1.35,
                  minHeight: 38,
                  boxShadow: sel ? `0 1px 4px ${alpha(th.color, 0.35)}` : 'none',
                  '&:hover': {
                    bgcolor: sel ? th.selectedBg : alpha(th.color, 0.22),
                    borderColor: alpha(th.color, 0.65)
                  }
                }}
              >
                {v.label}
              </Button>
            );
          })}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            p: 1.25,
            bgcolor: alpha(theme.palette.background.default, theme.palette.mode === 'dark' ? 0.45 : 0.65),
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}`
          }}
        >
          {JOB_LIST_VIEWS.map((v) => {
            const sel = activeListView === v.value;
            const th = v.theme;
            const borderCol = alpha(th.color, sel ? 0.55 : 0.4);
            return (
              <Button
                key={v.value}
                size="small"
                onClick={() => handleSelectListView(v.value)}
                sx={{
                  textTransform: 'none',
                  fontWeight: sel ? 700 : 600,
                  fontSize: '0.78rem',
                  lineHeight: 1.25,
                  color: sel ? (th.selectedColor || '#fff') : th.color,
                  bgcolor: sel ? th.selectedBg : th.defaultBg,
                  border: `1px solid ${borderCol}`,
                  borderRadius: 1,
                  py: 0.85,
                  px: 1.35,
                  minHeight: 38,
                  boxShadow: sel ? `0 1px 4px ${alpha(th.color, 0.35)}` : 'none',
                  '&:hover': {
                    bgcolor: sel ? th.selectedBg : alpha(th.color, 0.22),
                    borderColor: alpha(th.color, 0.65)
                  }
                }}
              >
                {v.label}
              </Button>
            );
          })}
        </Box>
      </Card>

      <Card sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField placeholder="Search jobs..." value={search} onChange={(e) => setSearch(e.target.value)} size="small" sx={{ minWidth: 300 }} InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ color: 'text.secondary' }} /></InputAdornment>) }} />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Priority</InputLabel>
            <Select value={filters.priority} label="Priority" onChange={(e) => setFilters({ ...filters, priority: e.target.value })}>
              <MenuItem value="">All</MenuItem>
              {(lookupOptions.priority.length ? lookupOptions.priority : [
                { code: 'critical', label: 'Critical' },
                { code: 'high', label: 'High' },
                { code: 'medium', label: 'Medium' },
                { code: 'low', label: 'Low' }
              ]).map((x) => (
                <MenuItem key={x.code} value={x.code}>{x.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <ListDetailOpenModeBar value={detailOpenMode} onChange={persistDetailOpenMode} label="Open Record In" />
        </Box>
      </Card>

      {pendingAssignmentRequests.length > 0 ? (
        <Paper sx={{ mb: 2, p: 2 }} variant="outlined">
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Pending assignment requests
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            Approve or decline employees who asked to join jobs that already have the maximum direct assignees.
          </Typography>
          <Stack divider={<Divider flexItem />} spacing={1.25}>
            {pendingAssignmentRequests.map((r) => (
              <Box
                key={r.id}
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Typography variant="body2" sx={{ flex: '1 1 220px' }}>
                  <strong>
                    {[r.requester?.first_name, r.requester?.last_name].filter(Boolean).join(' ').trim() ||
                      r.requester?.email ||
                      'Employee'}
                  </strong>{' '}
                  →{' '}
                  <strong>{r.job?.job_code || '—'}</strong>
                  {r.job?.title ? ` · ${r.job.title}` : ''}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button size="small" variant="contained" color="success" onClick={() => handleDecideAssignmentRequest(r.id, true)}>
                    Approve
                  </Button>
                  <Button size="small" variant="outlined" color="inherit" onClick={() => handleDecideAssignmentRequest(r.id, false)}>
                    Decline
                  </Button>
                </Stack>
              </Box>
            ))}
          </Stack>
        </Paper>
      ) : null}

      <Card sx={{ overflow: 'hidden' }}>
        <GeneralItemsGridFrame
          sx={{
            height: { xs: 440, sm: 520 },
            maxHeight: 'min(70vh, 720px)',
            minHeight: 320,
          }}
        >
          <ErpDataGrid
            listApplet
            columnOrderStorageKey="general-items-jobs"
            apiRef={apiRef}
            rows={jobs}
            columns={columns}
            loading={loading}
            paginationMode="server"
            rowCount={pagination.total}
            paginationModel={{ page: pagination.page, pageSize: pagination.pageSize }}
            onPaginationModelChange={(model) => setPagination({ ...pagination, ...model })}
            pageSizeOptions={[10, 25, 50]}
            getRowId={(row) => row.id}
            {...(isAdmin
              ? {
                  checkboxSelection: true,
                  rowSelectionModel,
                  onRowSelectionModelChange: setRowSelectionModel,
                  disableRowSelectionOnClick: true
                }
              : { disableRowSelectionOnClick: true })}
            rowHeight={88}
            getRowClassName={(params) => {
              const base = `erp-job-row erp-job-priority-${params.row.priority || 'medium'}`;
              return params.row.id === highlightJobId ? `${base} erp-job-row-highlight` : base;
            }}
            sx={{
              height: '100%',
              width: '100%',
              '& .MuiDataGrid-row': { cursor: 'pointer' },
              '& .MuiDataGrid-cell[data-field="assignees"]': { alignItems: 'center', py: 0.5 },
              '& .MuiDataGrid-cell[data-field="assignToMe"]': { alignItems: 'flex-start', py: 0.5 },
              '& .erp-job-row-highlight': {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: -2,
                transition: 'outline 0.15s ease'
              },
              '& .erp-job-priority-critical': {
                backgroundColor: PRIORITY_ROW_BG.critical,
                boxShadow: 'inset 3px 0 0 #dc2626'
              },
              '& .erp-job-priority-critical:hover': { backgroundColor: alpha('#ef4444', 0.26) },
              '& .erp-job-priority-high': { backgroundColor: PRIORITY_ROW_BG.high },
              '& .erp-job-priority-high:hover': { backgroundColor: alpha('#f59e0b', 0.22) },
              '& .erp-job-priority-medium': { backgroundColor: PRIORITY_ROW_BG.medium },
              '& .erp-job-priority-medium:hover': { backgroundColor: alpha('#3b82f6', 0.14) },
              '& .erp-job-priority-low': { backgroundColor: PRIORITY_ROW_BG.low },
              '& .erp-job-priority-low:hover': { backgroundColor: alpha('#10b981', 0.18) }
            }}
            onRowClick={(params) => handleOpenJobDetails(params.row)}
          />
        </GeneralItemsGridFrame>
      </Card>

      </Box>

      <Drawer
        anchor="right"
        open={detailOpenMode === 'side' && Boolean(sideId)}
        onClose={() => setSideId(null)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 580, md: 640 },
            maxWidth: '100vw',
            boxSizing: 'border-box'
          }
        }}
      >
        <Box sx={{ height: '100%', overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          {sideId ? (
            <JobDetailsPanel jobId={sideId} embedded onClose={() => setSideId(null)} />
          ) : null}
        </Box>
      </Drawer>

      <Dialog fullWidth maxWidth="lg" open={Boolean(popupId)} onClose={() => setPopupId(null)} scroll="paper">
        <DialogContent sx={{ p: 0, minHeight: 400 }}>
          {popupId ? (
            <JobDetailsPanel jobId={popupId} embedded onClose={() => setPopupId(null)} />
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(statusDialog)}
        onClose={() => {
          if (!statusSaving) {
            setStatusDialog(null);
            setClosureReason('');
          }
        }}
      >
        <DialogTitle>{statusDialog === 'closed' ? 'Close job?' : 'Cancel job?'}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Set <strong>{jobForCloseCancel?.job_code}</strong> — {jobForCloseCancel?.title} to{' '}
            <strong>{statusDialog === 'closed' ? 'Closed' : 'Cancelled'}</strong>?
          </Typography>
          <TextField
            label="Reason"
            placeholder="Explain why this job is being closed or cancelled…"
            value={closureReason}
            onChange={(e) => setClosureReason(e.target.value)}
            fullWidth
            required
            multiline
            minRows={3}
            disabled={statusSaving}
          />
        </DialogContent>
        <DialogActions>
          <Button
            disabled={statusSaving}
            onClick={() => {
              setStatusDialog(null);
              setClosureReason('');
            }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            color={statusDialog === 'cancelled' ? 'error' : 'secondary'}
            disabled={statusSaving || !String(closureReason || '').trim()}
            onClick={applyJobStatus}
          >
            {statusSaving ? 'Saving…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialog} onClose={() => { setDeleteDialog(false); setSelectedJob(null); }}>
        <DialogTitle>Delete Job</DialogTitle>
        <DialogContent><Typography>Are you sure you want to delete "{selectedJob?.title}"? This cannot be undone.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteDialog(false); setSelectedJob(null); }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default JobsList;
